import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSetlistPerformance } from '../hooks/useSetlistPerformance';
import { useAutoScroll, calculateScrollRate } from '../hooks/useAutoScroll';
import { useFitFontSize } from '../hooks/useFitFontSize';
import { useSyncSession } from '../hooks/useSyncSession';
import type { PerformanceState, AutoScrollStatus } from '../types/sync';
import type { LineGroup } from './performance/types';
import { buildGroups } from './performance/buildGroups';
import { GroupDisplay } from './performance/GroupDisplay';
import { nextPromptableIndex, prevPromptableIndex, nearestPromptableIndex } from './performance/types';

/** Number of upcoming lyric groups shown with reduced opacity. */
const UPCOMING_PREVIEW_COUNT = 3;

export default function SetlistPerformanceView() {
  const { setListId } = useParams<{ setListId: string }>();
  const navigate = useNavigate();

  const { state, actions } = useSetlistPerformance(setListId);
  const { status, error, songs, currentSongIndex } = state;
  const { goToNextSong, goToPreviousSong, goToSong } = actions;

  const currentSong = status === 'ready' ? songs[currentSongIndex] : null;

  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const lyricsElRef = useRef<HTMLDivElement | null>(null);

  // Build line groups for the current song
  const groups: LineGroup[] = useMemo(
    () => buildGroups(currentSong?.lines ?? []),
    [currentSong],
  );
  const totalGroups = groups.length;

  // Compute a font size that fits the longest line in the song
  const { fontSize: perfFontSize, ref: fitRef } = useFitFontSize(groups);

  // Combined ref callback for the lyrics div
  const lyricsRef = useCallback(
    (node: HTMLDivElement | null) => {
      lyricsElRef.current = node;
      fitRef(node);
    },
    [fitRef],
  );

  // ---- Auto-scroll integration ----

  const lineHeightEstimate = useRef(120);

  const maxScrollPosition = useMemo(
    () => Math.max(0, (totalGroups - 1) * lineHeightEstimate.current),
    [totalGroups],
  );

  const handlePositionChange = useCallback(
    (position: number) => {
      const idx = Math.round(position / lineHeightEstimate.current);
      const clamped = Math.max(0, Math.min(idx, totalGroups - 1));
      setCurrentGroupIndex(nearestPromptableIndex(groups, clamped));
    },
    [totalGroups, groups],
  );

  const { state: scrollState, start, pause, resume, stop, setPosition } = useAutoScroll(
    handlePositionChange,
    maxScrollPosition,
  );

  const hasTempo = typeof currentSong?.tempo === 'number' && currentSong.tempo > 0;
  const hasLength =
    (typeof currentSong?.lengthMinutes === 'number' && currentSong.lengthMinutes > 0) ||
    (typeof currentSong?.lengthSeconds === 'number' && currentSong.lengthSeconds > 0);
  const canAutoScroll = hasTempo && hasLength && totalGroups > 0;

  // ---- Sync session integration ----

  const leaderDisconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [leaderGracePeriod, setLeaderGracePeriod] = useState(false);

  // Ref to hold the latest received auto-scroll state for deferred application
  const receivedAutoScrollRef = useRef<PerformanceState | null>(null);

  const handleStateReceived = useCallback((performanceState: PerformanceState) => {
    // Apply song index
    goToSong(performanceState.currentSongIndex);
    // Apply line group index
    setCurrentGroupIndex(performanceState.currentLineGroupIndex);
    setPosition(performanceState.currentLineGroupIndex * lineHeightEstimate.current);
    // Apply auto-scroll status — handled via a ref so we can call start/pause/stop
    receivedAutoScrollRef.current = performanceState;
  }, [goToSong, setPosition]);

  const { state: syncState, actions: syncActions } = useSyncSession(setListId, handleStateReceived);

  // Apply auto-scroll status from received state (need currentSong for rate calc)
  useEffect(() => {
    const received = receivedAutoScrollRef.current;
    if (!received) return;
    receivedAutoScrollRef.current = null;

    if (received.autoScrollStatus === 'playing' && currentSong && canAutoScroll) {
      const rate = calculateScrollRate(
        currentSong.tempo!,
        currentSong.lengthMinutes ?? 0,
        currentSong.lengthSeconds ?? 0,
        maxScrollPosition,
      );
      if (rate > 0) start(rate);
    } else if (received.autoScrollStatus === 'paused') {
      pause();
    } else if (received.autoScrollStatus === 'stopped') {
      stop();
    }
  });

  // Derive follower flag — controls are disabled when actively following
  const isFollower = syncState.role === 'follower' && syncState.status === 'connected' && !leaderGracePeriod;

  // Handle leader disconnect grace period (5s before re-enabling controls)
  useEffect(() => {
    if (syncState.error === 'Leader disconnected') {
      setLeaderGracePeriod(true);
      leaderDisconnectTimerRef.current = setTimeout(() => {
        setLeaderGracePeriod(false);
      }, 5000);
    }

    return () => {
      if (leaderDisconnectTimerRef.current) {
        clearTimeout(leaderDisconnectTimerRef.current);
        leaderDisconnectTimerRef.current = null;
      }
    };
  }, [syncState.error]);

  // Re-enable controls immediately when session ends (status goes to disconnected)
  useEffect(() => {
    if (syncState.status === 'disconnected') {
      setLeaderGracePeriod(false);
      if (leaderDisconnectTimerRef.current) {
        clearTimeout(leaderDisconnectTimerRef.current);
        leaderDisconnectTimerRef.current = null;
      }
    }
  }, [syncState.status]);

  /** Derive the current auto-scroll status from scrollState flags. */
  const getAutoScrollStatus = useCallback((): AutoScrollStatus => {
    if (scrollState.isScrolling) return 'playing';
    if (scrollState.isPaused) return 'paused';
    return 'stopped';
  }, [scrollState.isScrolling, scrollState.isPaused]);

  // Reset line position and stop auto-scroll when song changes (Req 4.4)
  useEffect(() => {
    stop();
    setCurrentGroupIndex(0);
  }, [currentSongIndex, stop]);

  // Listen for fullscreen changes
  useEffect(() => {
    function onFsChange() {
      setIsFullScreen(!!document.fullscreenElement);
    }
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // ---- Song navigation with auto-scroll stop (Req 2.2, 2.3, 4.4) ----

  const handleNextSong = useCallback(() => {
    stop();
    goToNextSong();
    syncActions.broadcastState({
      currentSongIndex: Math.min(currentSongIndex + 1, songs.length - 1),
      currentLineGroupIndex: 0,
      autoScrollStatus: 'stopped',
    });
  }, [stop, goToNextSong, syncActions, currentSongIndex, songs.length]);

  const handlePreviousSong = useCallback(() => {
    stop();
    goToPreviousSong();
    syncActions.broadcastState({
      currentSongIndex: Math.max(currentSongIndex - 1, 0),
      currentLineGroupIndex: 0,
      autoScrollStatus: 'stopped',
    });
  }, [stop, goToPreviousSong, syncActions, currentSongIndex]);

  // ---- Auto-scroll controls ----

  const handlePlay = useCallback(() => {
    if (!currentSong || !canAutoScroll) return;
    const rate = calculateScrollRate(
      currentSong.tempo!,
      currentSong.lengthMinutes ?? 0,
      currentSong.lengthSeconds ?? 0,
      maxScrollPosition,
    );
    if (rate <= 0) return;
    start(rate);
    syncActions.broadcastState({
      currentSongIndex,
      currentLineGroupIndex: currentGroupIndex,
      autoScrollStatus: 'playing',
    });
  }, [currentSong, canAutoScroll, maxScrollPosition, start, syncActions, currentSongIndex, currentGroupIndex]);

  const handlePauseResume = useCallback(() => {
    if (scrollState.isPaused) {
      resume();
      syncActions.broadcastState({
        currentSongIndex,
        currentLineGroupIndex: currentGroupIndex,
        autoScrollStatus: 'playing',
      });
    } else if (scrollState.isScrolling) {
      pause();
      syncActions.broadcastState({
        currentSongIndex,
        currentLineGroupIndex: currentGroupIndex,
        autoScrollStatus: 'paused',
      });
    }
  }, [scrollState, pause, resume, syncActions, currentSongIndex, currentGroupIndex]);

  const handleStop = useCallback(() => {
    stop();
    setCurrentGroupIndex(0);
    syncActions.broadcastState({
      currentSongIndex,
      currentLineGroupIndex: 0,
      autoScrollStatus: 'stopped',
    });
  }, [stop, syncActions, currentSongIndex]);

  // ---- Line navigation (prev / next line within current song) ----

  const handlePrevLine = useCallback(() => {
    const newIdx = prevPromptableIndex(groups, currentGroupIndex);
    setCurrentGroupIndex(newIdx);
    setPosition(newIdx * lineHeightEstimate.current);
    syncActions.broadcastState({
      currentSongIndex,
      currentLineGroupIndex: newIdx,
      autoScrollStatus: getAutoScrollStatus(),
    });
  }, [currentGroupIndex, groups, setPosition, syncActions, currentSongIndex, getAutoScrollStatus]);

  const handleNextLine = useCallback(() => {
    const newIdx = nextPromptableIndex(groups, currentGroupIndex);
    setCurrentGroupIndex(newIdx);
    setPosition(newIdx * lineHeightEstimate.current);
    syncActions.broadcastState({
      currentSongIndex,
      currentLineGroupIndex: newIdx,
      autoScrollStatus: getAutoScrollStatus(),
    });
  }, [currentGroupIndex, groups, setPosition, syncActions, currentSongIndex, getAutoScrollStatus]);

  // ---- Exit ----

  const handleExit = useCallback(() => {
    navigate('/setlists');
  }, [navigate]);

  // ---- Keyboard navigation (Req 5.3, 7.1, 7.2, 7.3) ----

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Disable all navigation shortcuts when in follower role
      if (isFollower) {
        if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', ' '].includes(e.key)) {
          e.preventDefault();
          return;
        }
      }

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          handleNextSong();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePreviousSong();
          break;
        case 'ArrowDown':
          e.preventDefault();
          handleNextLine();
          break;
        case 'ArrowUp':
          e.preventDefault();
          handlePrevLine();
          break;
        case ' ':
          e.preventDefault();
          if (scrollState.isScrolling) pause();
          else if (scrollState.isPaused) resume();
          else if (canAutoScroll) handlePlay();
          break;
        case 'Escape':
          if (!document.fullscreenElement) {
            handleExit();
          }
          break;
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isFollower,
    handleNextSong, handlePreviousSong, handleNextLine, handlePrevLine,
    scrollState, pause, resume, canAutoScroll, handlePlay, handleExit,
  ]);

  // ---- Mouse wheel / touch for line navigation ----

  useEffect(() => {
    const el = lyricsElRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      if (isFollower) return;
      const delta = e.deltaY > 0 ? 1 : -1;
      setCurrentGroupIndex((prev) => {
        const next = delta > 0
          ? nextPromptableIndex(groups, prev)
          : prevPromptableIndex(groups, prev);
        setPosition(next * lineHeightEstimate.current);
        return next;
      });
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [groups, setPosition, isFollower]);

  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isFollower) return;
    touchStartY.current = e.touches[0].clientY;
  }, [isFollower]);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (isFollower) return;
      if (touchStartY.current === null) return;
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) > 30) {
        const delta = diff > 0 ? 1 : -1;
        setCurrentGroupIndex((prev) => {
          const next = delta > 0
            ? nextPromptableIndex(groups, prev)
            : prevPromptableIndex(groups, prev);
          setPosition(next * lineHeightEstimate.current);
          return next;
        });
      }
      touchStartY.current = null;
    },
    [groups, setPosition, isFollower],
  );

  const toggleFullScreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement && containerRef.current) {
        await containerRef.current.requestFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch { /* blocked by browser */ }
  }, []);

  // Determine visible groups
  const visibleStart = currentGroupIndex;
  const visibleEnd = Math.min(totalGroups, currentGroupIndex + 4 + UPCOMING_PREVIEW_COUNT);

  const isAtFirstSong = currentSongIndex === 0;
  const isAtLastSong = currentSongIndex === songs.length - 1;
  const isPlaying = scrollState.isScrolling;
  const isPaused = scrollState.isPaused;

  // ---- Render: loading / error / empty states ----

  if (status === 'loading') {
    return (
      <div className="perf-container" role="status">
        <p className="perf-loading">Loading setlist…</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="perf-container" role="alert">
        <p className="perf-error">{error ?? 'An error occurred.'}</p>
        <button className="btn btn-secondary" onClick={handleExit} type="button">
          Go Back
        </button>
      </div>
    );
  }

  if (status === 'empty' || !currentSong) {
    return (
      <div className="perf-container">
        <p className="perf-error">No valid songs in this setlist.</p>
        <button className="btn btn-secondary" onClick={handleExit} type="button">
          Go Back
        </button>
      </div>
    );
  }

  // ---- Render: ready state ----

  return (
    <div
      className="perf-container"
      ref={containerRef}
      role="region"
      aria-label={`Setlist performance: ${state.setList?.name ?? 'Untitled'}`}
    >
      {/* Toolbar: exit, song indicator, fullscreen */}
      <div className="perf-toolbar">
        <button
          className="btn btn-secondary perf-back-btn"
          onClick={handleExit}
          type="button"
          aria-label="Exit setlist performance"
        >
          ✕
        </button>

        {/* Current Song Indicator (Req 3.1, 3.2, 3.3) */}
        <span className="perf-song-title">{currentSong.title ?? 'Untitled'}</span>
        <span className="perf-setlist-position" aria-live="polite">
          Song {currentSongIndex + 1} of {songs.length}
        </span>

        {/* Sync status indicators (Req 6.1, 6.2, 6.3) — centered in toolbar */}
        <div className="perf-sync-status" aria-label="Sync session status">
          {syncState.status === 'connecting' && (
            <span className="perf-sync-connecting" aria-live="polite">Connecting…</span>
          )}

          {syncState.status === 'connected' && syncState.role === 'leader' && (
            <>
              <span className="perf-sync-badge perf-sync-badge-leader" aria-live="polite">
                Leading
              </span>
              <button
                className="btn btn-danger perf-sync-end-btn"
                onClick={() => syncActions.endSession()}
                type="button"
                aria-label="End sync session"
              >
                End Session
              </button>
            </>
          )}

          {syncState.status === 'connected' && syncState.role === 'follower' && (
            <span className="perf-sync-badge perf-sync-badge-follower" aria-live="polite">
              Following
            </span>
          )}

          {syncState.error === 'Leader disconnected' && (
            <span className="perf-sync-notification" role="alert" aria-live="assertive">
              Leader has disconnected
            </span>
          )}

          {syncState.status === 'error' && syncState.error !== 'Leader disconnected' && (
            <span className="perf-sync-error" role="alert" aria-live="assertive">
              {syncState.error}
            </span>
          )}
        </div>

        <span className="perf-line-counter" aria-live="polite">
          {currentGroupIndex + 1} / {totalGroups}
        </span>

        <button
          className="btn btn-secondary perf-fs-btn"
          onClick={toggleFullScreen}
          type="button"
          aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
        >
          {isFullScreen ? '⊡' : '⛶'}
        </button>
      </div>

      {/* Song Navigation Controls (Req 2.1, 2.4, 2.5) are integrated into perf-controls below */}

      {/* Lyrics display */}
      <div
        className="perf-lyrics"
        ref={lyricsRef}
        style={{ '--perf-font-size': `${perfFontSize}px` } as React.CSSProperties}
        aria-live="polite"
        aria-atomic="false"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {totalGroups === 0 ? (
          <p className="perf-error">No lyrics available for this song.</p>
        ) : (
          groups.slice(visibleStart, visibleEnd).map((group, offset) => {
            const absIndex = visibleStart + offset;
            const isCurrent = absIndex === currentGroupIndex;
            const isUpcoming =
              absIndex > currentGroupIndex &&
              absIndex <= currentGroupIndex + UPCOMING_PREVIEW_COUNT;
            return (
              <GroupDisplay
                key={absIndex}
                group={group}
                isCurrent={isCurrent}
                isUpcoming={isUpcoming}
              />
            );
          })
        )}
      </div>

      {/* Line navigation + song navigation + auto-scroll controls */}
      <div className="perf-controls">
        <button
          className="btn btn-secondary"
          onClick={handlePreviousSong}
          disabled={isAtFirstSong || isFollower}
          type="button"
          aria-label="Previous song"
        >
          ◀◀ Prev Song
        </button>

        <button
          className="btn btn-secondary"
          onClick={handlePrevLine}
          disabled={currentGroupIndex === 0 || isFollower}
          type="button"
          aria-label="Previous line"
        >
          ▲ Prev
        </button>

        {canAutoScroll && !isPlaying && !isPaused && !isFollower && (
          <button
            className="btn btn-primary perf-play-btn"
            onClick={handlePlay}
            type="button"
            aria-label="Start auto-scroll"
          >
            ▶ Play
          </button>
        )}
        {(isPlaying || isPaused) && !isFollower && (
          <>
            <button
              className="btn btn-primary perf-pause-btn"
              onClick={handlePauseResume}
              type="button"
              aria-label={isPaused ? 'Resume auto-scroll' : 'Pause auto-scroll'}
            >
              {isPaused ? '▶ Resume' : '⏸ Pause'}
            </button>
            <button
              className="btn btn-danger perf-stop-btn"
              onClick={handleStop}
              type="button"
              aria-label="Stop auto-scroll and reset"
            >
              ⏹ Stop
            </button>
          </>
        )}

        {!canAutoScroll && !isPlaying && !isPaused && !isFollower && (
          <span className="perf-no-scroll-hint" aria-live="polite">
            Set tempo &amp; length to enable auto-scroll
          </span>
        )}

        <button
          className="btn btn-secondary"
          onClick={handleNextLine}
          disabled={currentGroupIndex >= totalGroups - 1 || isFollower}
          type="button"
          aria-label="Next line"
        >
          ▼ Next
        </button>

        <button
          className="btn btn-secondary"
          onClick={handleNextSong}
          disabled={isAtLastSong || isFollower}
          type="button"
          aria-label="Next song"
        >
          Next Song ▶▶
        </button>
      </div>
    </div>
  );
}
